import React, { useCallback, useEffect, useState } from 'react';
import { AudienceAutocomplete, JGroupApi, jGroupList } from 'jamespot-user-api';
import { useAbortController } from '../../hooks/useAbortController';
import { useIntl } from 'react-intl';
import { JRCButton } from '../JRCButton/JRCButton';
import { JRCGroupList, JRCGroupListProps } from '../JRCGroupList/JRCGroupList';

export type JRCGroupsProps = {
    handlers: { group: Pick<JGroupApi, 'list'> };
    onError?: (message?: string) => void;
    initialGroups: AudienceAutocomplete[];
    onSelectGroup?: (group: AudienceAutocomplete[]) => void;
    publicOnly?: boolean;
};

const LIMIT_PER_PAGE = 8;

const groupToAutocomplete = (group: Required<JRCGroupListProps>['list']['data'][number]): AudienceAutocomplete => ({
    title: group.title,
    Pseudo: group.title,
    _url: group._url,
    id: String(group.id),
    uri: group.uri,
    shortUri: group.uri,
    name: group.title,
    mainType: 'spot',
    type: 'spot',
    label: group.title,
    cssClass: '',
    cssColor: '',
    class: '',
});

export const JRCGroups = ({ handlers, onError, initialGroups, onSelectGroup, publicOnly }: JRCGroupsProps) => {
    const [isLoading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [groups, setGroups] = useState<JRCGroupListProps['list']>();
    const [selectedGroups, setSelectedGroups] = useState<AudienceAutocomplete[]>(initialGroups);
    const [query, setQuery] = useState<string>();

    const intl = useIntl();
    const abortController = useAbortController();

    const selectGroup = useCallback(
        (group: Required<JRCGroupListProps>['list']['data'][number]) => {
            setSelectedGroups((selected) => {
                const newSelected = selected.some((u) => u.uri === group.uri)
                    ? selected.filter((u) => u.uri !== group.uri)
                    : [...selected, groupToAutocomplete(group)];
                if (onSelectGroup) {
                    onSelectGroup(newSelected);
                }
                return newSelected;
            });
        },
        [onSelectGroup],
    );

    useEffect(() => {
        setLoading(true);
        handlers.group
            .list<jGroupList>(
                {
                    page,
                    limit: LIMIT_PER_PAGE,
                    format: 'raw-list',
                    public: publicOnly,
                    query,
                    filters: [{ name: 'disable', operator: '!=', value: 1, isNull: true }],
                },
                { signal: abortController.signal },
            )
            .then((res) => {
                setGroups({
                    limit: LIMIT_PER_PAGE,
                    page: page,
                    data: res.result.data,
                    cnt: res.result.cnt,
                });
                setLoading(false);
            })
            .catch((e) => {
                if (onError && !abortController.signal.aborted) {
                    onError(e.errorMsg || intl.formatMessage({ id: 'GLOBAL_Error' }));
                }
            });
    }, [abortController.signal, handlers.group, intl, onError, page, publicOnly, query]);

    return (
        <JRCGroupList
            isLoading={isLoading}
            onPageChange={setPage}
            list={groups}
            onClick={(group) => selectGroup(group)}
            actionComponent={(group) => (
                <JRCButton
                    label={intl.formatMessage({ id: 'GLOBAL_Select' })}
                    onClick={() => selectGroup(group)}
                    variant={selectedGroups.some((sg) => sg.uri === group.uri) ? 'contained' : 'outlined'}
                />
            )}
            onSearch={setQuery}
        />
    );
};
